import api from './api';

/**
 * Service for the nominee's own nominations: the students a faculty member
 * named on an awarded application, once the program's decisions are released.
 * Both routes act only on the signed-in student's nominations.
 */
export const nominationService = {
    /**
     * The caller's nominations, as { nominations, total, commitments }.
     * `commitments` is the student's weekly commitment for each term of the
     * nominations, keyed by term code.
     */
    async getMine() {
        const response = await api.get('/nomination/mine');
        return response.data;
    },

    /**
     * The nominations on one awarded application, as { nominations }. The
     * program's coordinators and admins get every field; the application's
     * faculty, once decisions are released, get names, emails and statuses.
     * @param {string} applicationId
     */
    async getForApplication(applicationId) {
        const response = await api.get(
            `/nomination/application/${applicationId}`
        );
        return response.data;
    },

    /**
     * Accept or decline a nomination. An accept carries the student's
     * answers to the university's student-employment requirements; a decline
     * may carry a reason. A key whose value is undefined is not sent.
     * @param {string} nominationId
     * @param {{ action: 'accept' | 'decline', reason?: string,
     *           employmentAttestation?: object,
     *           requirementAttestations?: object[], commitments?: object,
     *           isReturning?: boolean, canAttendOrientation?: boolean,
     *           canAttendPedagogy?: boolean, confirmsAccuracy?: boolean }} answer
     */
    async respond(nominationId, { action, reason, ...answers }) {
        const body = { action };
        if (reason !== undefined) body.reason = reason;
        // The answers an accept carries: the employment attestation and the
        // program terms. A key whose value is undefined is not sent.
        for (const [key, value] of Object.entries(answers)) {
            if (value !== undefined) body[key] = value;
        }
        const response = await api.post(
            `/nomination/${nominationId}/respond`,
            body
        );
        return response.data;
    },

    /**
     * End an accepted nomination with a reason. The nominee gives their own
     * place up; a reviewer releases it. The server refuses (409) when the
     * nomination is no longer in the status the caller was shown.
     * @param {string} nominationId
     * @param {{ reasonCode: string, reasonText?: string }} reason
     * @param {{ status: string }} shown - the status the caller was shown
     */
    async release(nominationId, { reasonCode, reasonText }, shown) {
        const body = { reasonCode, status: shown.status };
        if (typeof reasonText === 'string') body.reasonText = reasonText;
        const response = await api.post(
            `/nomination/${nominationId}/release`,
            body
        );
        return response.data;
    },

    /**
     * Take back an offer the nominee has not answered (an offer still open or
     * one that expired). The place opens and the nominee is emailed.
     * @param {string} nominationId
     * @param {string} reason - 1 to 500 characters; only coordinators read it
     */
    async takeBack(nominationId, reason) {
        const response = await api.post(
            `/nomination/${nominationId}/take-back`,
            { reason }
        );
        return response.data;
    },

    /**
     * Offer the place to a nominee: one not yet offered, one taken back or
     * released, or the same nominee again after the offer expired.
     * @param {string} nominationId
     * @param {{ respondByDay?: string, releaseFrom?: string }} [options] -
     *   `respondByDay` is the last day to answer, YYYY-MM-DD (none means the
     *   program's default); `releaseFrom` is the expired nomination whose
     *   hold on the place this offer takes over in one step. A key left
     *   undefined is not sent.
     */
    async offer(nominationId, { respondByDay, releaseFrom } = {}) {
        const body = {};
        if (respondByDay) body.respondByDay = respondByDay;
        if (releaseFrom) body.releaseFrom = releaseFrom;
        const response = await api.post(
            `/nomination/${nominationId}/offer`,
            body
        );
        return response.data;
    },

    /**
     * Give an expired offer's place back to the students who applied.
     * @param {string} nominationId
     */
    async releaseExpired(nominationId) {
        const response = await api.post(
            `/nomination/${nominationId}/release`,
            {}
        );
        return response.data;
    },
};
